/**
 * The grounded-problem view model — a backend-neutral view of a grounded, solved
 * (or attempted) optimization/feasibility problem, for the elements panel to
 * render without knowing which solver produced it.
 *
 * Both backends adapt to it:
 *   - {@link lpGroundedProblem} wraps the linear `Model` + HiGHS solution (DCOPF);
 *   - {@link acGroundedProblem} wraps the residual system + Newton result (ACPF).
 *
 * The panel consumes the same three table shapes in either mode — variables,
 * constraints, and (LP only) objective cost categories — so the "base data →
 * rules → constraints → solution" structure is one code path across modes. The
 * pieces that genuinely differ are carried as nullable fields: `objective` (LP
 * only, since AC power flow has no objective) and `convergence` (AC only, since
 * Newton iterates where the LP returns a single global optimum).
 */

import { Model, HighsRawSolution, Variable } from './highs';
import { extractSolutionTables, SolutionVariableTable } from './solutionVariables';
import { extractConstraintTables, SolutionConstraintTable } from './solutionConstraints';
import { extractObjectiveTables, ObjectiveTable } from './objectiveTerms';
import { GroundedResidual, VarMeta, rnodeToString } from './nonlinear/residual';
import { NewtonResult } from './nonlinear/newton';

/** Newton convergence, for the AC path — the ‖F‖∞ trace and the outcome. */
export interface ConvergenceInfo {
  converged: boolean;
  iterations: number;
  residualNorm: number;
  /** ‖F‖∞ after each iteration. */
  history: number[];
}

/** A grounded problem, ready to tabulate, independent of the solver behind it. */
export interface GroundedProblem {
  kind: 'lp' | 'ac';
  /** Decision variables grouped by class, joined with their solved values. */
  variables: SolutionVariableTable[];
  /** Constraints (LP) or residual equations (AC) grouped by class. */
  constraints: SolutionConstraintTable[];
  /** Objective cost categories — LP only; `null` for AC power flow. */
  objective: ObjectiveTable[] | null;
  /** Newton convergence — AC only; `null` for the LP. */
  convergence: ConvergenceInfo | null;
  counts: { variables: number; constraints: number };
  status: { valid: boolean; message: string };
}

/** Adapt a linear model + HiGHS solution (DCOPF) to the view model. */
export function lpGroundedProblem(
  model: Model,
  solution: HighsRawSolution | undefined,
  valid: boolean,
  message: string,
): GroundedProblem {
  return {
    kind: 'lp',
    variables: extractSolutionTables(model.variables, solution),
    constraints: extractConstraintTables(model.constraints, solution),
    objective: extractObjectiveTables(model, solution),
    convergence: null,
    counts: { variables: model.variables.length, constraints: model.constraints.length },
    status: { valid, message },
  };
}

/** The grounded residual system, as {@link groundResiduals} returns it. */
export interface GroundedResidualSystem {
  residuals: GroundedResidual[];
  variables: string[];
  variableMeta: Map<string, VarMeta>;
}

/** Adapt a residual system + Newton result (ACPF) to the view model. */
export function acGroundedProblem(
  system: GroundedResidualSystem,
  result: NewtonResult,
): GroundedProblem {
  // Reuse the LP variable-table extractor by synthesizing the same shapes it
  // reads: variables carrying group/indices, and a solution keyed by name.
  const variables: Variable[] = system.variables.map((name) => {
    const meta = system.variableMeta.get(name);
    return { name, group: meta?.group, indices: meta?.indices };
  });
  const asSolution: HighsRawSolution = {
    Status: result.converged ? 'Optimal' : 'NotConverged',
    Columns: Object.fromEntries(
      system.variables.map((name) => [name, { Primal: result.x.get(name) }]),
    ),
  };

  const message = result.converged
    ? `Converged in ${result.iterations} iterations (‖F‖∞ = ${result.residualNorm.toExponential(2)})`
    : `Did not converge from this start (‖F‖∞ = ${result.residualNorm.toExponential(2)})`;

  return {
    kind: 'ac',
    variables: extractSolutionTables(variables, asSolution),
    constraints: residualTables(system.residuals),
    objective: null,
    convergence: {
      converged: result.converged,
      iterations: result.iterations,
      residualNorm: result.residualNorm,
      history: result.history,
    },
    counts: { variables: system.variables.length, constraints: system.residuals.length },
    status: { valid: result.converged, message },
  };
}

/**
 * Group grounded residuals into constraint tables — one per rule, each row the
 * residual equation `<expr> = 0`. The `dual` column stays empty (residuals have
 * no shadow price), so the panel simply doesn't render it.
 */
function residualTables(residuals: GroundedResidual[]): SolutionConstraintTable[] {
  const tables = new Map<string, SolutionConstraintTable>();
  for (const r of residuals) {
    let table = tables.get(r.group);
    if (!table) {
      table = { name: r.group, columns: [], rows: [] };
      tables.set(r.group, table);
    }
    for (const column of Object.keys(r.indices)) {
      if (!table.columns.includes(column)) table.columns.push(column);
    }
    table.rows.push({
      name: r.name,
      indices: r.indices,
      definition: `${rnodeToString(r.node)} = 0`,
      dual: undefined,
    });
  }
  const result = Array.from(tables.values());
  for (const table of result) {
    table.rows.sort((a, b) => compareByColumns(a.indices, b.indices, table.columns));
  }
  return result;
}

/** Order rows by their index columns: numbers numerically, everything else lexically. */
function compareByColumns(
  a: Record<string, string | number>,
  b: Record<string, string | number>,
  columns: string[],
): number {
  for (const column of columns) {
    const av = a[column];
    const bv = b[column];
    if (av === bv) continue;
    if (typeof av === 'number' && typeof bv === 'number') return av - bv;
    return String(av ?? '').localeCompare(String(bv ?? ''));
  }
  return 0;
}
